// library imports
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { PageContent, PageHeader, PageWrapper, PageContainer } from 'components/common/layout_element/LayoutElement';
import * as Constants from "_core/configs/Constants";
import { FormatDateTime, GetEncodedCSV, DownloadCsvAsSJIS, DownloadCsvAsUtf8BOM } from "_core/utils/common";

// User Component imports
import dataManagerInstance from '_core/managers/DataManager';
import {Row, Button, TextInput, Stack, Card, Icon, Uploader, LoadingBox, Text, FileDownLoadButton, CommonModal, FlexibleTextInput} from "_core/components/common/UI";
import { InfoCard } from "components/common/parts";
import { setLoading, setMenuState, setErrorModal, setInitializeState } from "_core/slices/scene/SceneSlice";

// styles imports
import './AdminWelcomeMsgEdit.scss';
import * as SceneConfig from "_core/configs/SceneConfig";
import AdminWelcomMsgEditApi from "apis/AdminWelcomeMsgEditApi";


const AdminWelcomeMsgEdit = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const ExecAdminWelcomeMsgEditApi = AdminWelcomMsgEditApi();

    const { initializeState } = useSelector((store) => store.scene);

    // JSONデータを保持するステート
    const [msgData, setMsgData] = useState({});
    // 初期データと比較して変更があるか確認するためのバックアップ
    const [originData, setOriginData] = useState({});

    // refferIdに該当する見出し
    const DISPLAY_NAME_MAP = {
        "1": "応募者数を増やしたい",
        "2": "歩留を改善したい",
        "3": "単価を抑えたい",
        "4": "問題点を知りたい",
        "5": "改善策を知りたい",
        "default": "デフォルトメッセージ",
    };



    useEffect(() => {
        dispatch(setMenuState({ isHeader: false, isMenu: true, isFooter: false, isFullScreen: false }));
    }, [dispatch]);
    
    useEffect(() => {
        if(initializeState !== Constants.INITIALIZE_STATE.allComplete) {
            return;
        }

        // --- データ取得と初期化 ---
        try {
            const jsonStr = dataManagerInstance.getWelcomeMsgJson();
            const parsedData = JSON.parse(jsonStr || "{}");
            setMsgData(parsedData);
            setOriginData(parsedData); // 変更検知用
        } catch (e) {
            console.error("JSONのパースに失敗しました", e);
            setMsgData({});
        }
        dispatch(setInitializeState(Constants.INITIALIZE_STATE.pageDataFetchComplete))
    }, [initializeState]);

    // 入力値が変更された時のハンドラ
    const handleValueChange = (key, value) => {
        setMsgData(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const AdminWelcomMsgEditApiRequest = async () => {

        // 通信中の表示を出す
        dispatch(setLoading(true));

        const requestParams = {
            welcome_message: JSON.stringify(msgData)
        };

        const result = await ExecAdminWelcomeMsgEditApi(requestParams);
        // 通信中の表示を消す
        dispatch(setLoading(false));
        if (result.error || (result.response && result.response.error_code > 0))
        {
            // エラーモーダル表示
            dispatch(setErrorModal({ show: true, text: result.response.error_detail }));
        } else {
            // 正常終了時はオリジナルデータを更新して、更新ボタンを非活性にする
            setOriginData(msgData);
            // 必要に応じて完了メッセージの表示など
        }
    };

    // 更新ボタンの活性化判定
    const GetCanUpdate = () => {
        // 初期データと現在のデータが異なる場合にtrueを返す
        return JSON.stringify(msgData) !== JSON.stringify(originData);
    };

    return (
        <PageContent pageId="AdminWelcomeMsgEdit">
            <PageHeader>
                <Stack className="spacing-4px">
                    <div className="page-header-title">WelcomeMessage</div>
                    <div className="page-header-detail">ウェルカムメッセージの編集・更新が行えます。</div>
                </Stack>
            </PageHeader>
            <PageWrapper>
                <PageContainer>
                    {/* ここにページ内コンテンツのコンポーネントを記述 */}
                    <Stack className="block-center spacing-32px">
                        <Card className="base-card">
                            <Stack className="spacing-16px">
                                {Object.keys(msgData).map((key) => (
                                    <Row key={key} className="input-row-area align-start spacing-16px">
                                        {/* 左側：Keyの表示 */}
                                        <Stack className="input-label-area stack flex-center margin-top-12px">
                                            <div className="input-label input-label-text text-size-medium text-bold">
                                                {DISPLAY_NAME_MAP[key] || key}
                                            </div>
                                        </Stack>
                                        
                                        {/* 右側：Valueの編集（テキストエリア） */}
                                        <Stack className="input-form-area stack flex-center">
                                            <FlexibleTextInput 
                                                multiline={true} // 複数行表示用（コンポーネントの仕様に合わせる）
                                                rows={3}         // 3行表示
                                                value={msgData[key]} // stateから値を紐付け
                                                default={msgData[key]}
                                                placeholder="メッセージを入力してください"
                                                isLoading={(initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete)}
                                                onchange={(e) => handleValueChange(key, e)}
                                                rules={[
                                                    { type: "required"},
                                                    { type: "maxLength", value: 500},
                                                ]}
                                                maxHeight={100}
                                            />
                                        </Stack>
                                    </Row>
                                ))}
                                
                                {Object.keys(msgData).length === 0 && (
                                    <div className="text-center">編集可能なデータがありません。</div>
                                )}
                            </Stack>

                            <Stack className="align-center margin-top-60px">
                                <Button 
                                    className={"width-320px " + (GetCanUpdate() ? "primary": "primary disabled")}
                                    onClick={() => AdminWelcomMsgEditApiRequest()}
                                    disabled={!GetCanUpdate}
                                >
                                    更新
                                </Button>
                            </Stack>
                        </Card>
                    </Stack>
                </PageContainer>
            </PageWrapper>
        </PageContent>
    );
};

export default AdminWelcomeMsgEdit;